import { useEffect, useState } from "react";
import { gigsApi } from "../../lib/gigs";
import type { GigListItemResponse, GigPosterProfileResponse } from "../../types/gigTypes";
import { formatDateTime, formatEstimatedDuration, formatMonthYear } from "../../utils/DateUtils";
import { formatGigScheduleSummary } from "../../utils/GigScheduleUtils";
import { formatRoundedGigCompensation } from "../../utils/GigCompensationUtils";
import { formatCityStateZip } from "../../utils/LocationUtils";
import shareIcon from "../../assets/icons/share.svg";
import Button from "../ui/Button/Button";
import Modal from "../ui/Modal/Modal";

export type RequestFeedback = {
  tone: "success" | "error";
  message: string;
};

type GigDetailGridProps = {
  gig: GigListItemResponse;
  canRequestAsTeen: boolean;
  isOwnGig?: boolean;
  isRequesting: boolean;
  hasRequested: boolean;
  feedback: RequestFeedback | null;
  onRequestGig: () => void;
};

const buildGigShareUrl = (gig: GigListItemResponse) => {
  const path = "/gigs";
  const params = new URLSearchParams();
  if (gig.volunteer) {
    params.set("board", "VOLUNTEER_GIGS");
  }
  params.set("gigId", gig.id);

  if (typeof window === "undefined") {
    return `${path}?${params.toString()}`;
  }

  const url = new URL(path, window.location.origin);
  params.forEach((value, key) => url.searchParams.set(key, value));
  return url.toString();
};

const copyTextToClipboard = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const copied = document.execCommand("copy");
    if (!copied) {
      throw new Error("Copy command failed");
    }
  } finally {
    document.body.removeChild(textarea);
  }
};

const isShareCancelled = (error: unknown) =>
  error instanceof DOMException && error.name === "AbortError";

const canUseNativeShare = (shareData: ShareData) =>
  typeof navigator !== "undefined"
  && typeof navigator.share === "function"
  && (typeof navigator.canShare !== "function" || navigator.canShare(shareData));

const shareGig = async (gig: GigListItemResponse): Promise<"shared" | "copied" | "cancelled"> => {
  const shareUrl = buildGigShareUrl(gig);
  const shareData: ShareData = {
    title: gig.title,
    text: `Check out this gig on MyTeenGig: ${gig.title}`,
    url: shareUrl,
  };

  if (canUseNativeShare(shareData)) {
    try {
      await navigator.share(shareData);
      return "shared";
    } catch (error) {
      if (isShareCancelled(error)) {
        return "cancelled";
      }
    }
  }

  await copyTextToClipboard(shareUrl);
  return "copied";
};

const GigDetailGrid = ({
  gig,
  canRequestAsTeen,
  isOwnGig = false,
  isRequesting,
  hasRequested,
  feedback,
  onRequestGig,
}: GigDetailGridProps) => {
  const canRequestThisGig = canRequestAsTeen && !isOwnGig && gig.status === "PUBLISHED" && !hasRequested;
  const compensationLabel = formatRoundedGigCompensation(gig.paymentAmountCents, gig.volunteer);
  const posterName = gig.posterFirstName?.trim() || "Poster";
  const posterRatingLabel = typeof gig.posterRating === "number" ? `${gig.posterRating.toFixed(1)}★` : "No rating yet";
  const posterVerificationLabel = gig.posterIdentityVerified ? "Verified" : "Unverified";
  const [isImagesModalOpen, setIsImagesModalOpen] = useState(false);
  const [isPosterProfileModalOpen, setIsPosterProfileModalOpen] = useState(false);
  const [posterProfile, setPosterProfile] = useState<GigPosterProfileResponse | null>(null);
  const [isLoadingPosterProfile, setIsLoadingPosterProfile] = useState(false);
  const [posterProfileError, setPosterProfileError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const onRequestClick = () => {
    if (!canRequestThisGig || isRequesting) {
      return;
    }
    onRequestGig();
  };

  useEffect(() => {
    if (!isImagesModalOpen || gig.images.length <= 1) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        setActiveImageIndex((current) => (current === 0 ? gig.images.length - 1 : current - 1));
      }
      if (event.key === "ArrowRight") {
        setActiveImageIndex((current) => (current === gig.images.length - 1 ? 0 : current + 1));
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [gig.images.length, isImagesModalOpen]);

  const openImagesModal = () => {
    setActiveImageIndex(0);
    setIsImagesModalOpen(true);
  };

  const showPreviousImage = () => {
    setActiveImageIndex((current) => (current === 0 ? gig.images.length - 1 : current - 1));
  };

  const showNextImage = () => {
    setActiveImageIndex((current) => (current === gig.images.length - 1 ? 0 : current + 1));
  };

  const activeImage = gig.images[activeImageIndex] ?? null;
  const posterProfileName = posterProfile?.firstName?.trim() || posterName;
  const posterProfileLocation = posterProfile ? formatCityStateZip(posterProfile, "Location not available") : "Location not available";
  const posterProfileInitial = posterProfileName.charAt(0).toUpperCase() || "P";
  const posterProfileRating = typeof posterProfile?.averageRating === "number" ? posterProfile.averageRating.toFixed(1) : "N/A";
  const posterMemberSince = formatMonthYear(posterProfile?.memberSince ?? null, "Not available");
  const [shareFeedback, setShareFeedback] = useState<RequestFeedback | null>(null);

  useEffect(() => {
    if (!isPosterProfileModalOpen) {
      return;
    }
    if (!gig.posterUserId) {
      setPosterProfile(null);
      setPosterProfileError("Poster profile is unavailable for this gig.");
      return;
    }

    let cancelled = false;
    const loadPosterProfile = async () => {
      try {
        setIsLoadingPosterProfile(true);
        setPosterProfileError(null);
        const response = await gigsApi.getPosterProfile(gig.id);
        if (cancelled) {
          return;
        }
        setPosterProfile(response);
      } catch {
        if (cancelled) {
          return;
        }
        setPosterProfile(null);
        setPosterProfileError("We couldn't load the poster profile right now.");
      } finally {
        if (!cancelled) {
          setIsLoadingPosterProfile(false);
        }
      }
    };

    void loadPosterProfile();
    return () => {
      cancelled = true;
    };
  }, [gig.id, gig.posterUserId, isPosterProfileModalOpen]);

  const onShareGig = async () => {
    try {
      const result = await shareGig(gig);
      if (result === "cancelled") {
        return;
      }
      setShareFeedback({
        tone: "success",
        message: result === "shared" ? "Gig shared." : "Gig link copied.",
      });
    } catch {
      setShareFeedback({ tone: "error", message: "Unable to share this gig. Try again from your browser controls." });
    }
  };

  return (
    <>
      <div className="rounded-lg border border-myteengig-border bg-myteengig-background p-4">
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <section className="flex flex-col items-start justify-start rounded-lg border border-myteengig-border bg-white p-4 text-left">
          <h3 className="w-full text-left text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Description</h3>
          <p className="mt-2 min-h-28 w-full whitespace-pre-wrap break-words text-left text-sm text-myteengig-text-primary">
            {gig.description}
          </p>
          {gig.requiresEquipment ? (
            <div className="mt-4 w-full rounded-lg border border-myteengig-border bg-myteengig-background/70 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Equipment or supplies</p>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm text-myteengig-text-primary">
                {gig.equipmentDescription?.trim() || "Equipment is required, but details were not provided."}
              </p>
            </div>
          ) : null}
          {gig.volunteer ? (
            <div className="mt-4 w-full rounded-lg border border-myteengig-border bg-myteengig-background/70 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Community service hours</p>
              <p className="mt-2 text-sm font-medium text-myteengig-text-primary">
                {gig.communityServiceHoursEligible ? "Available" : "Not offered"}
              </p>
              {gig.communityServiceHoursEligible ? (
                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-myteengig-text-primary">
                  {gig.communityServiceProofDetails}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>

          <aside className="rounded-lg border border-myteengig-border bg-white p-4">
            <Button
              variant="outline"
              onClick={() => setIsPosterProfileModalOpen(true)}
              className="w-full bg-myteengig-background text-myteengig-text-primary hover:border-myteengig-primary hover:text-myteengig-primary"
            >
              <span className="flex w-full flex-col items-center justify-center gap-1 text-center">
                <span>{posterName} • {posterRatingLabel} • {posterVerificationLabel}</span>
                <span className="text-xs font-medium uppercase tracking-wide text-myteengig-text-muted">View poster profile</span>
              </span>
            </Button>

            <dl className="mt-4 space-y-3 text-sm text-myteengig-text-primary">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Timing Details</dt>
                <dd className="mt-1">{formatGigScheduleSummary(gig)}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Compensation</dt>
                <dd className="mt-1">{compensationLabel}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Estimated Time</dt>
                <dd className="mt-1">{formatEstimatedDuration(gig.estimatedDurationMinutes)}</dd>
              </div>
            </dl>

            <div className="mt-4 space-y-3">
              {gig.images.length > 0 ? (
                <Button
                  variant="outline"
                  onClick={openImagesModal}
                  className="w-full bg-white text-myteengig-text-primary hover:border-myteengig-primary hover:text-myteengig-primary"
                >
                  See Images ({gig.images.length})
                </Button>
              ) : null}

              <Button
                variant="outline"
                onClick={() => {
                  void onShareGig();
                }}
                className="flex w-full items-center justify-center gap-2 bg-white text-myteengig-text-primary hover:border-myteengig-primary hover:text-myteengig-primary"
                aria-label={`Share ${gig.title}`}
              >
                <img src={shareIcon} alt="" className="h-4 w-4" aria-hidden="true" />
                <span>Share This Gig</span>
              </Button>

              {canRequestAsTeen ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    void onRequestClick();
                  }}
                  disabled={!canRequestThisGig || isRequesting}
                  className="w-full"
                >
                  {isOwnGig ? "Your posting" : hasRequested ? "Applied" : isRequesting ? "Applying..." : "Apply for Gig"}
                </Button>
              ) : null}

              {!canRequestThisGig && !hasRequested && gig.status !== "PUBLISHED" ? (
                <p className="mt-2 text-xs text-myteengig-text-muted">Only published gigs can be requested.</p>
              ) : null}
            </div>

            {shareFeedback ? (
              <p className={`mt-3 text-sm font-medium ${shareFeedback.tone === "success" ? "text-emerald-700" : "text-red-600"}`}>
                {shareFeedback.message}
              </p>
            ) : null}

            {feedback ? (
              <p className={`mt-3 text-sm font-medium ${feedback.tone === "success" ? "text-emerald-700" : "text-red-600"}`}>
                {feedback.message}
              </p>
            ) : null}
          </aside>
        </div>
      </div>
      <Modal
        isOpen={isPosterProfileModalOpen}
        onClose={() => setIsPosterProfileModalOpen(false)}
        title={`${posterName}'s Profile`}
        panelClassName="max-w-3xl"
      >
        {isLoadingPosterProfile ? (
          <div className="py-8 text-center text-sm text-myteengig-text-muted">Loading poster profile...</div>
        ) : posterProfileError ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">{posterProfileError}</div>
        ) : posterProfile ? (
          <div className="space-y-6">
            <section className="rounded-2xl border border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(191,239,255,0.35),_transparent_28%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(244,250,255,1))] p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-myteengig-border bg-white text-2xl font-bold text-myteengig-primary sm:mx-0">
                  {posterProfile.profilePhoto ? (
                    <img src={posterProfile.profilePhoto.url} alt={`${posterProfileName} profile`} className="h-full w-full object-cover" />
                  ) : (
                    <span>{posterProfileInitial}</span>
                  )}
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                    <h3 className="text-2xl font-semibold text-myteengig-text-primary">{posterProfileName}</h3>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                        posterProfile.identityVerified
                          ? "border border-emerald-200 bg-emerald-50 text-emerald-800"
                          : "border border-slate-200 bg-slate-50 text-slate-700"
                      }`}
                    >
                      {posterProfile.identityVerified ? "ID VERIFIED" : "UNVERIFIED"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    {posterProfileLocation} • Member since {posterMemberSince}
                  </p>
                </div>
              </div>
            </section>

            <section className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Poster rating</p>
                <p className="mt-2 text-2xl font-bold text-myteengig-text-primary">{posterProfileRating}</p>
              </div>
              <div className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Reviews received</p>
                <p className="mt-2 text-2xl font-bold text-myteengig-text-primary">{posterProfile.totalReviews}</p>
              </div>
              <div className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Gigs posted</p>
                <p className="mt-2 text-2xl font-bold text-myteengig-text-primary">{posterProfile.postedGigCount}</p>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-center gap-3 text-center">
                <div>
                  <h4 className="text-lg font-semibold text-myteengig-text-primary">Recent teen feedback</h4>
                  <p className="text-sm text-myteengig-text-muted">A quick look at how teens have rated working with this poster.</p>
                </div>
              </div>
              {posterProfile.recentReviews.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {posterProfile.recentReviews.map((review) => (
                    <div key={review.reviewId} className="rounded-2xl border border-myteengig-border bg-white px-4 py-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="font-semibold text-myteengig-text-primary">{review.gigTitle ?? "Completed gig"}</p>
                          <p className="mt-1 text-sm text-myteengig-text-muted">
                            {review.reviewerFirstName || "Teen reviewer"} • {formatDateTime(review.createdAt)}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-myteengig-primary">{review.rating.toFixed(1)} / 5</p>
                      </div>
                      <p className="mt-3 text-sm leading-6 text-myteengig-text-primary">
                        {review.message?.trim() ? review.message : "No written feedback was included with this rating."}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background/60 px-4 py-6 text-center text-sm text-myteengig-text-muted">
                  No poster reviews have been left yet.
                </div>
              )}
            </section>
          </div>
        ) : null}
      </Modal>
      <Modal
        isOpen={isImagesModalOpen}
        onClose={() => setIsImagesModalOpen(false)}
        title={`${gig.title} Photos`}
        panelClassName="max-w-5xl"
        bodyClassName="pr-0"
      >
        {activeImage ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-myteengig-text-muted">
                Image {activeImageIndex + 1} of {gig.images.length}
              </p>
              {gig.images.length > 1 ? (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={showPreviousImage}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" onClick={showNextImage}>
                    Next
                  </Button>
                </div>
              ) : null}
            </div>

            <div
              className="overflow-hidden rounded-2xl border border-myteengig-border bg-myteengig-background"
              onTouchStart={(event) => setTouchStartX(event.changedTouches[0]?.clientX ?? null)}
              onTouchEnd={(event) => {
                const endX = event.changedTouches[0]?.clientX ?? null;
                if (touchStartX === null || endX === null || gig.images.length <= 1) {
                  setTouchStartX(null);
                  return;
                }

                const delta = endX - touchStartX;
                if (Math.abs(delta) >= 40) {
                  if (delta > 0) {
                    showPreviousImage();
                  } else {
                    showNextImage();
                  }
                }
                setTouchStartX(null);
              }}
            >
              <img
                src={activeImage.url}
                alt={activeImage.originalFileName || gig.title}
                className="max-h-[70vh] w-full object-contain bg-slate-950"
              />
            </div>

            {gig.images.length > 1 ? (
              <div className="grid gap-3 sm:grid-cols-4">
                {gig.images.map((image, index) => (
                  <button
                    key={image.id}
                    type="button"
                    onClick={() => setActiveImageIndex(index)}
                    className={`overflow-hidden rounded-xl border transition ${
                      index === activeImageIndex
                        ? "border-myteengig-primary ring-2 ring-myteengig-primary/20"
                        : "border-myteengig-border"
                    }`}
                  >
                    <img src={image.url} alt={image.originalFileName || gig.title} className="h-24 w-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default GigDetailGrid;
